import { SecurityUtils } from "Global/Base/Security/SecurityUtils";
import { LazyLoadingSectionLogger, LoggingSection } from "Logging/index";
import { InvalidArgumentException, InvalidOperationException } from "../../Global/Base/Exceptions";
import { IDisposable } from "../../Global/Base/IDisposable";
import {
    NativeAppException,
    NativeAppExitedException,
    NativeAppNoCertificateFoundException,
    NativeAppSelectionParamsException,
    NativeAppTooManyCertificatesFoundException,
    NativeAppUnavailableException,
} from "./CryptoExceptions";
import { ISigningAppCommunicator } from "./CryptoInterfaces";
import { CommandType } from "./Messages/CommandType";
import { ICommandMessage, IErrorResult, IMessageBody, IMessageEnvelope, IOpenSession, IResultMessage } from "./Messages/MessagesInterface";
import { ResultType } from "./Messages/ResultType";

const logger = LazyLoadingSectionLogger.create(LoggingSection.cryptoWebExtension);

/**
 * Komunikační třída, která je pouhým kanálem pro komunikaci mezi doplňkem a stránkou
 */
export class SigningExtensionCommunicator implements ISigningAppCommunicator, IDisposable {
    /**
     * Řídí maximální délku jednotlivých logovaných záznamů
     */
    public static loggingMaxLength?: number = 200;

    private static readonly _appIdentification = "MultiWebCryptoExtension";
    private static readonly _errorClosedPort = "Attempting to use a disconnected port object";
    private static readonly _errorHostNotFound = "Specified native messaging host not found.";
    private static readonly _errorNativeAppNotFound = "No such native application cz.tescosw.nativeapp.crypto"; // nenainstalovaná appka, FF 57+
    private static readonly _errorUnexpectedErr = "An unexpected error occurred"; //TC: Firefox, nainstalovat nativku s extension a pak smazat spustitelný soubor nativky
    private static readonly _errorExtensionPermission = "This extension does not have permission to use native application"; //TC: Firefox nainstalovat nativku s extension a smazat json konfig pro native message.
    private static readonly _errorHostExited = "Native host has exited.";
    private static readonly _errorSelectionParams = "selectionParams";
    private static readonly _errorNoCertificateFound = "No certificate found.";
    private static readonly _errorTooManyCertificatesFound = "More than 1 certificate found.";
    private static readonly _errorUnknown = "Unknown";
    //private static readonly _errorNoConnectionFound= 'No connection found';
    //private static readonly _errorHostForbidden = 'Access to the specified native messaging host is forbidden.';

    private _sessionId: string;
    private _messageListener: ((e: MessageEvent) => any) | null;
    private _pendingRequests: Map<string, { resolver: Function; rejecter: Function }> = new Map();
    /** Továrna, která vrací metodu pro logování uživatelských akcí.*/
    private _userInteractionLoggerFactory: (() => (e: MessageEvent) => void) | undefined;

    private __userActionLogger: ((e: MessageEvent) => void) | undefined | null = undefined;
    private get _userActionLogger(): ((e: MessageEvent) => void) | undefined | null {
        if (this.__userActionLogger == undefined) {
            try {
                this.__userActionLogger = this._userInteractionLoggerFactory ? this._userInteractionLoggerFactory() : null;
            } catch {
                this.__userActionLogger = null;
            }
        }
        return this.__userActionLogger;
    }

    /**
     * Unikátní identifikátor session
     */
    public set sessionId(value: string) {
        this._sessionId = value;
    }

    public get sessionId(): string {
        return this._sessionId;
    }

    constructor(userInteractionLoggerFactory?: () => (e: MessageEvent) => void) {
        window.addEventListener("message", this._messageListener = (e) => this._onMessage(e));
        this._userInteractionLoggerFactory = userInteractionLoggerFactory;
    }

    /**
     * Přijetí zprávy od window
     */
    private _onMessage(e: MessageEvent): void {
        if (this._userActionLogger)
            this._userActionLogger(e);
        const message = e.data as IMessageEnvelope;

        // zprava od nekoho jineho, nez je nase extension
        if (!message || message.responseSource !== SigningExtensionCommunicator._appIdentification)
            return;

        if (!message.response)
            throw new InvalidOperationException("Received message without response body");

        // zprava pro jinou session
        if ((this._sessionId && message.sessionId !== this._sessionId))
            return;

        //Pokud nema zprava ID tak, ji nejde najit mezi pendingRequest a reagovat na ni
        if (!message.response.resultId) {
            logger.info(`Received message: ${JSON.stringify(message.response)}`);

            // pokud je zpráva bez ID typu Error, tak došlo pravděpodobně k fatální chybě a všechny čekající dotazy končí touto chybou
            if (message.response.resultType === ResultType[ResultType.Error] ||
                message.response.resultType === "ERROR") {
                for (const r of this._pendingRequests.values())
                    r.rejecter(this._createError(message.response.body as IErrorResult));
                this._pendingRequests.clear();
                return;
            }

            throw new InvalidOperationException("Received message without ID");
        }

        const request = this._pendingRequests.get(message.response.resultId);
        if (!request)
            return;

        logger.info(`Received message: ${JSON.stringify(message.response).substr(0, SigningExtensionCommunicator.loggingMaxLength)}`);

        this._pendingRequests.delete(message.response.resultId);

        if (message.response.resultType === ResultType[ResultType.Error] ||
            message.response.resultType === "ERROR")
            request.rejecter(this._createError(message.response.body as IErrorResult));
        else
            request.resolver(message);
    }

    /**
     * Vytvoří dlouhodobé spojení pro komunikaci s extension. Všechny následující příkazy budou patřit do této session.
     * Session je nutné uzavřít pomocí volání closeSession().
     * @param lang Nepovinný atribut určující jazyk GUI, který je potřebný pouze v případě, že se v rámci session bude otevírat GUI, jinak by se neměl posílat. Jazyk musí být ISO 639-1 (en, cs, ...).
     */
    public async openSession(lang?: string): Promise<void> {
        if (this._sessionId !== undefined)
            return;

        logger.info("Opening new session"); //`

        const body: IOpenSession | undefined = !lang ? undefined : { lang: lang };

        const resp = await this._postMessage(CommandType.OpenSession, body);

        if (!resp.sessionId) {
            this._sessionId = "";
            throw new InvalidOperationException("Received invalid session id from extension");
        }

        this._sessionId = resp.sessionId;

        logger.info(`Opened session ${this._sessionId}`); //`
    }

    /**
     * Uzavře existující spojení s extension. Je nutné volat vždy v páru s openSession().
     * Pokud žádné otevřené spojení neexistuje, tak dojde k tichému skončení.
     */
    public async closeSession(): Promise<void> {
        if (!this._sessionId)
            return;

        logger.info(`Closing session ${this._sessionId}`); //`

        try {
            await this.postMessage(CommandType.CloseSession);
            //await this.postMessage(CommandType.CloseApplication); // ukončení nativní aplikace
        } catch (e) {
            // už došlo k zavření (např. uživatelem), není potřeba zobrazovat chybu
            if (e instanceof Error && e.message !== SigningExtensionCommunicator._errorClosedPort)
                throw e;
        }

        logger.info(`Closed session ${this._sessionId}`); //`

        this._sessionId = "";
    }

    /**
     * Odešle zprávu do extension
     * @param message odesílaná zpráva
     */
    public async postMessage(command: CommandType, body?: IMessageBody): Promise<IResultMessage> {
        const envelope = await this._postMessage(command, body);
        if (!envelope.response)
            throw new InvalidOperationException("Received message without response body");

        return envelope.response;
    }

    /**
     * Zprávu zabalí do obálky, nalepí známku a pošle poštou
     * @param message Odesílaná zpráva
     * @returns Nerozbalená obálka s odpovědí
     */
    protected _postMessage(command: CommandType, body?: IMessageBody): Promise<IMessageEnvelope> {
        if (command == null)
            throw new InvalidArgumentException("command");

        const message = this._createMessage(command, body);

        logger.info(`Sending message: ${JSON.stringify(message).substr(0, SigningExtensionCommunicator.loggingMaxLength)}`); //`

        const requestId = message.id;

        const promise = new Promise<IMessageEnvelope>((resolve, reject) => {
            this._pendingRequests.set(requestId, { resolver: resolve, rejecter: reject });
            window.postMessage(this._createMessageEnvelope(message), window.location.toString()); // poslání pouze do content scriptu současného okna
        });

        return promise;
    }

    /**
     * Zprávu zabalí do obálky
     * @param message Odesílaná zpráva
     */
    protected _createMessageEnvelope(message: ICommandMessage): IMessageEnvelope {
        const newMessage: IMessageEnvelope = {
            sessionId: this._sessionId,
            source: SigningExtensionCommunicator._appIdentification,
            request: message,
        };

        return newMessage;
    }

    /**
     * Vytvoří obsah requestu zprávy
     * @param command typ zprávy
     * @param body tělo zprávy
     */
    protected _createMessage(command: CommandType, body?: IMessageBody): ICommandMessage {
        return {
            id: SigningExtensionCommunicator._generateId(),
            cmd: CommandType[command].toString(),
            body: body,
        };
    }

    private _createError(error: IErrorResult): Error {
        if (error.type === "Disconnected" && error.message === SigningExtensionCommunicator._errorHostNotFound ||
            error.type === "Disconnected" && error.message === SigningExtensionCommunicator._errorUnexpectedErr ||
            error.type === "Disconnected" && error.message === SigningExtensionCommunicator._errorNativeAppNotFound ||
            error.type === "Disconnected" && error.message && error.message.startsWith(SigningExtensionCommunicator._errorExtensionPermission))
            return new NativeAppUnavailableException();
        else if ((error.type === "Disconnected" && error.message === SigningExtensionCommunicator._errorHostExited) ||
            (error.type === "Disconnected" && error.message === SigningExtensionCommunicator._errorUnknown) ||
            error.message === SigningExtensionCommunicator._errorClosedPort)
            return new NativeAppExitedException();
        else if (error.type === "EInvalidCommand" && error.message === SigningExtensionCommunicator._errorSelectionParams)
            return new NativeAppSelectionParamsException();
        else if (error.type === "EInvalidCommand" && error.message === SigningExtensionCommunicator._errorNoCertificateFound)
            return new NativeAppNoCertificateFoundException();
        else if (error.type === "EInvalidCommand" && error.message === SigningExtensionCommunicator._errorTooManyCertificatesFound)
            return new NativeAppTooManyCertificatesFoundException();
        return new NativeAppException(error.message);
    }

    /**
     * Vygeneruje unikátní id pro zprávu
     */
    private static _generateId(): string {
        // po identifikátoru je požadováno, aby byl kryptograficky silný - zabránění hádání session a následnému vstoupení do ní
        return SecurityUtils.generateStrongKey(16);
    }

    public dispose(): void {
        this.closeSession().then(() => {
            if (this._messageListener) {
                window.removeEventListener("message", this._messageListener);
                this._messageListener = null;
            }
        });
    }
}
